// Build a viewer-core Vfs (Map<absolute-virtual-path, contents>) from
// user-selected files. Three input shapes are handled, each with the
// same output:
//
//   1. fileListToVfs   — <input type="file" [webkitdirectory] multiple>
//   2. dataTransferToVfs — drag-and-drop (supports nested folders via
//      webkitGetAsEntry + recursive directory walk)
//   3. (single-file pick collapses to fileListToVfs)
//
// Filter: `.arch`, `package.archspace`, `.md`/`.markdown` documents, and
// image assets (svg/png/jpg/gif/webp) a doc can reference. Anything else
// (JS, random text) is silently dropped.
//
// Path normalization: paths are POSIX, absolute, rooted at "/". If the
// pick contains a single top-level directory (the common "user dragged
// a project folder" case), we strip it so paths look like the package
// root. Otherwise paths preserve whatever relative structure was given.

import { isMarkdownPath, type Vfs } from "@archlang/viewer-core";
import { type Assets, isAssetFile } from "./workspaceAssets.js";

export interface LoadedSources {
  readonly vfs: Vfs;
  /** Binary assets (images) a `.md` doc references — bytes, not text. */
  readonly assets: Assets;
  /** Short human-readable label of what the user opened. */
  readonly label: string;
  /** Filenames that were rejected by the extension filter. */
  readonly skipped: readonly string[];
}

const KEEP_BASENAMES = new Set(["package.archspace"]);

/** A raw path/content pair before filtering + normalization. Image assets
 *  carry their bytes (`Uint8Array`); text source files carry a string. */
export interface RawEntry {
  readonly path: string;
  readonly content: string | Uint8Array;
}

/** True for files the viewer keeps (`.arch`, `package.archspace`, `.md`/
 *  `.markdown`, or an image asset). */
export function isWorkspaceFile(basename: string): boolean {
  return isInteresting(basename);
}

/**
 * The shared core of every loader: filter to workspace files, normalize
 * paths to POSIX-absolute, strip a common top directory, and derive a
 * label. `fileListToVfs`, `dataTransferToVfs`, and the remote-zip loader
 * all funnel through here so they behave identically.
 */
export function buildWorkspaceFromEntries(
  entries: Iterable<RawEntry>,
  opts: { readonly label?: string } = {},
): LoadedSources {
  const textMap = new Map<string, string>();
  const assetMap = new Map<string, Uint8Array>();
  const skipped: string[] = [];
  for (const { path, content } of entries) {
    const basename = path.split("/").pop() ?? path;
    if (!isInteresting(basename)) {
      skipped.push(path);
      continue;
    }
    const vpath = toVirtualPath(path);
    if (isAssetFile(basename)) {
      // Bytes preferred; a string source (rare) is encoded to bytes so the
      // asset channel is uniformly binary.
      assetMap.set(vpath, typeof content === "string" ? new TextEncoder().encode(content) : content);
    } else {
      textMap.set(vpath, typeof content === "string" ? content : new TextDecoder().decode(content));
    }
  }
  // Strip a shared top-level directory across BOTH channels with one decision.
  const top = commonTopDir([...textMap.keys(), ...assetMap.keys()]);
  const vfs = applyStrip(textMap, top);
  const assets = applyStrip(assetMap, top);
  return {
    vfs,
    assets,
    label: opts.label ?? deriveLabelFromPaths([...vfs.keys(), ...assets.keys()]),
    skipped,
  };
}

function deriveLabelFromPaths(paths: readonly string[]): string {
  if (paths.length === 0) return "(empty)";
  if (paths.length === 1) return paths[0].split("/").pop() ?? paths[0];
  return `${paths.length} files`;
}

// File.prototype.text() is missing in jsdom and very old Safari. Fall
// back to FileReader, which both jsdom and ancient browsers support.
function readAsText(file: File): Promise<string> {
  if (typeof file.text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error ?? new Error("FileReader error"));
    r.onload = () => resolve(String(r.result ?? ""));
    r.readAsText(file);
  });
}

/** Read a file's raw bytes (asset channel). Mirrors `readAsText`'s fallback. */
async function readAsBytes(file: File): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === "function") {
    return new Uint8Array(await file.arrayBuffer());
  }
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error ?? new Error("FileReader error"));
    r.onload = () => resolve(new Uint8Array(r.result as ArrayBuffer));
    r.readAsArrayBuffer(file);
  });
}

/** Read a picked file as bytes (assets) or text (source) by its name. */
async function readEntry(path: string, file: File): Promise<RawEntry> {
  const name = path.split("/").pop() ?? path;
  return { path, content: isAssetFile(name) ? await readAsBytes(file) : await readAsText(file) };
}

function isInteresting(name: string): boolean {
  if (KEEP_BASENAMES.has(name)) return true;
  return name.endsWith(".arch") || isMarkdownPath(name) || isAssetFile(name);
}

/** Turn whatever raw "path" the browser gave us into a POSIX virtual path. */
function toVirtualPath(raw: string): string {
  // Collapse backslashes, strip leading "./", ensure single leading "/".
  let p = raw.replace(/\\/g, "/").replace(/^\.\/+/, "");
  if (!p.startsWith("/")) p = "/" + p;
  return p;
}

/** The single top-level directory every path lives under, or null. Only a
 *  genuine directory counts — every path needs ≥2 segments sharing the same
 *  first one. A single file at the root (e.g. `/checkout.arch`) yields null.
 *  Computed over the UNION of the text + asset keys so both channels strip the
 *  same prefix. */
function commonTopDir(keys: readonly string[]): string | null {
  if (keys.length === 0) return null;
  const segments = keys.map((k) => k.split("/").filter(Boolean));
  const top = segments[0]?.[0];
  if (!top) return null;
  for (const segs of segments) {
    if (segs.length < 2 || segs[0] !== top) return null;
  }
  return top;
}

/** Remap a path→value map by stripping `top` (a no-op when `top` is null). */
function applyStrip<V>(map: Map<string, V>, top: string | null): Map<string, V> {
  if (top === null) return map;
  const re = new RegExp(`^/${escapeRegExp(top)}(?=/|$)`);
  const out = new Map<string, V>();
  for (const [k, v] of map) out.set(k.replace(re, ""), v);
  return out;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export async function fileListToVfs(files: FileList | File[]): Promise<LoadedSources> {
  const arr = Array.from(files);
  const entries: RawEntry[] = [];
  for (const f of arr) {
    // webkitRelativePath is set when the user picked via
    // <input webkitdirectory>; falls back to plain file name otherwise.
    const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
    entries.push(await readEntry(rel, f));
  }
  const result = buildWorkspaceFromEntries(entries);
  return { ...result, label: deriveLabel(arr, result.vfs.size + result.assets.size) };
}

export async function dataTransferToVfs(dt: DataTransfer): Promise<LoadedSources> {
  // Prefer the entries API (handles directories); fall back to a flat
  // file list if the browser doesn't expose it.
  const items = Array.from(dt.items ?? []);
  const entries: FileSystemEntry[] = [];
  for (const it of items) {
    const entry = (it as DataTransferItem & {
      webkitGetAsEntry?: () => FileSystemEntry | null;
    }).webkitGetAsEntry?.();
    if (entry) entries.push(entry);
  }
  if (entries.length === 0) {
    return fileListToVfs(dt.files);
  }
  const flat: File[] = [];
  const skipped: string[] = [];
  for (const entry of entries) {
    await walkEntry(entry, "", flat, skipped);
  }
  const raw: RawEntry[] = [];
  for (const f of flat) {
    const path = (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
    raw.push(await readEntry(path, f));
  }
  // walkEntry already dropped non-workspace files (and noted them in
  // `skipped`), so nothing new is rejected here.
  const result = buildWorkspaceFromEntries(raw);
  return {
    vfs: result.vfs,
    assets: result.assets,
    label: deriveLabel(flat, result.vfs.size + result.assets.size),
    skipped,
  };
}

function deriveLabel(files: readonly File[], kept: number): string {
  if (files.length === 0) return "(empty)";
  const first = files[0]!;
  const rel = (first as File & { webkitRelativePath?: string }).webkitRelativePath || first.name;
  const topDir = rel.split("/")[0];
  if (rel.includes("/") && topDir) {
    return `${topDir} (${kept} file${kept === 1 ? "" : "s"})`;
  }
  if (files.length === 1) return first.name;
  return `${kept} files`;
}

// FileSystemEntry is the legacy DataTransfer entries API; types are
// minimal and not in lib.dom for every TS version. Re-declare the bits
// we need.
interface FileSystemEntry {
  isDirectory: boolean;
  isFile: boolean;
  name: string;
  fullPath: string;
}
interface FileSystemFileEntry extends FileSystemEntry {
  file(cb: (f: File) => void, err?: (e: Error) => void): void;
}
interface FileSystemDirectoryEntry extends FileSystemEntry {
  createReader(): FileSystemDirectoryReader;
}
interface FileSystemDirectoryReader {
  readEntries(
    cb: (entries: FileSystemEntry[]) => void,
    err?: (e: Error) => void
  ): void;
}

async function walkEntry(
  entry: FileSystemEntry,
  prefix: string,
  flat: File[],
  skipped: string[]
): Promise<void> {
  const full = prefix === "" ? entry.name : `${prefix}/${entry.name}`;
  if (entry.isFile) {
    const file = await readFileEntry(entry as FileSystemFileEntry);
    if (!isInteresting(file.name)) {
      skipped.push(full);
      return;
    }
    // Override webkitRelativePath so downstream paths reflect the walk.
    Object.defineProperty(file, "webkitRelativePath", {
      value: full,
      configurable: true,
    });
    flat.push(file);
    return;
  }
  if (entry.isDirectory) {
    if (entry.name === "node_modules" || entry.name === ".git" || entry.name === "dist") {
      return;
    }
    const children = await readDirEntries(entry as FileSystemDirectoryEntry);
    for (const child of children) {
      await walkEntry(child, full, flat, skipped);
    }
  }
}

function readFileEntry(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

function readDirEntries(entry: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const reader = entry.createReader();
    const all: FileSystemEntry[] = [];
    // readEntries returns up to ~100 entries per call; loop until empty.
    const drain = () => {
      reader.readEntries((batch) => {
        if (batch.length === 0) return resolve(all);
        all.push(...batch);
        drain();
      }, reject);
    };
    drain();
  });
}
